import {call, put} from '@redux-saga/core/effects';
import api from 'api';
import ApiService from 'api/ApiService';
import {unpackId} from 'common/diplomatIdUtil';
import {DialogResult, DialogTypes} from 'common/names';
import {ObjectSchema} from 'common/yup';
import {InputDialogData} from 'components/dialogs/InputDialog';
import {NameFormType} from 'components/dialogs/NameDialog';
import {FileManagerDialogData} from 'features/layout/DialogRoutes/dialogs/FileManagerDialog/FileManagerDialogWrapper';
import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {pushErrorToast, pushSuccessToast} from 'features/notifications/redux/actions';
import i18n from 'i18n';
import {isString} from 'lodash';
import {getPassword} from 'redux/common/saga/getPassword';
import {asyncSagaConstructor, unhandedSagaError} from 'redux/common';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';
import {FILE_SEPARATOR} from 'redux/server/hooks/useBrowseList';
import {IDiplomatFile} from 'types/DiplomatFile';
import {Action} from 'types/redux';
import {keyTypes} from '..';
import {
   ExportPgpPublicPayload,
   ExportPgpSecretPayload,
   ExportSshPublicPayload,
   ExportSshSecretPayload,
} from './exportKeySchemas';
import escapeFile from 'sanitize-filename';

const PUBLIC_TITLE = 'Export OpenPGP Public Key To';
const SECRET_TITLE = 'Export OpenPGP Key Pair To';
const SSH_PUBLIC_TITLE = 'Export SSH Client Public Key To';
const SSH_SECRET_TITLE = 'Export SSH Secret Key To';

const ASC_EXT = '.asc';
// const PGP_EXT = '.pgp';

const ASC_EXT_MATCHER = '*.asc';
const PGP_EXT_MATCHER = '*.pgp';

// const ASC_TYPE = 'ASCII Armored (*.asc)';
// const PGP_TYPE = 'OpenPGP (*.pgp)';
// const FILE_TYPES = {ASC_TYPE, PGP_TYPE};

const PUBLIC_EXT = '.pub';
const PUBLIC_EXT_MATCHER = '*.pub';
const ALL_EXT_MATCHER = '*.*';

// const PUBLIC_TYPE = 'Public SSH Key (*.pub)';
// const SECRET_TYPE = 'SSH Key Pair (*.*)';
// const SSH_PUBLIC_FILE_TYPES = {PUBLIC_TYPE};
// const SSH_SECRET_FILE_TYPES = {SECRET_TYPE};

type IHandler = {
   title: string;
   errTitle: string;
   buildFilename: (s: string) => string;
   filesOfType: string[];
   url: string;
   schema: ObjectSchema<any>;
};

const handlers: Record<string, IHandler> = {
   public: {
      title: PUBLIC_TITLE,
      errTitle: i18n.t('Error Exporting Public Key'),
      buildFilename: (name: string) => name + ASC_EXT,
      filesOfType: [ASC_EXT_MATCHER, PGP_EXT_MATCHER],
      url: api.exportPgpPublic,
      schema: ExportPgpPublicPayload,
   },
   secret: {
      title: SECRET_TITLE,
      errTitle: i18n.t('Error Exporting Key Pair'),
      buildFilename: (name: string) => name + ASC_EXT,
      filesOfType: [ASC_EXT_MATCHER, PGP_EXT_MATCHER],
      url: api.exportPgpSecret,
      schema: ExportPgpSecretPayload,
   },
   publicSsh: {
      title: SSH_PUBLIC_TITLE,
      errTitle: i18n.t('Error Exporting SSH Public Key'),
      buildFilename: (name: string) => name + PUBLIC_EXT,
      filesOfType: [PUBLIC_EXT_MATCHER],
      url: api.exportSshPublic,
      schema: ExportSshPublicPayload,
   },
   secretSsh: {
      title: SSH_SECRET_TITLE,
      errTitle: i18n.t('Error Exporting SSH Key Pair'),
      buildFilename: (name: string) => name,
      filesOfType: [ALL_EXT_MATCHER],
      url: api.exportSshSecret,
      schema: ExportSshSecretPayload,
   },
};

export type ExportKeyPayload = {
   key: INav;
   secret?: boolean;
   ssh?: boolean;
};

export function* exportSaga(action: Action<ExportKeyPayload>) {
   const {key, secret, ssh} = action.payload;
   const {id} = unpackId(key.id);
   let handler = handlers.public;
   if (ssh) {
      handler = secret ? handlers.secretSsh : handlers.publicSsh;
   } else {
      handler = secret ? handlers.secret : handlers.public;
   }
   const {title, buildFilename, filesOfType, errTitle, url, schema} = handler;
   let filePath: string = '';
   let passphrase: string;

   try {
      const escapedName = escapeFile(key.name, {replacement: '_'});
      const filename = buildFilename(escapedName);

      filePath = yield call(getFilePath, {
         title,
         filename,
         filesOfType,
      });
      if (!filePath) {
         return;
      }

      if (secret) {
         passphrase = yield call(
            getPassword,
            i18n.t('Passphrase'),
            i18n.t('Enter File Passphrase')
         );
         if (!passphrase) {
            return;
         }
      }

      yield call(
         asyncSagaConstructor(url, keyTypes.EXPORT, {
            getMethod: ApiService.postSecureJson,
            data: schema.cast(
               {
                  keyID: id,
                  asciiArmored: filePath.indexOf(ASC_EXT) > 0,
                  filePath,
                  overwrite: false,
                  passphrase: passphrase!,
               },
               {stripUnknown: true}
            ),
         })
      );

      yield put(
         pushSuccessToast(`Key ${key.name} successfully exported to
      ${filePath}`)
      );
   } catch (err) {
      // const e = yield err.clone().text();
      const e = JSON.stringify(err);
      if (e.indexOf('File already exists') >= 0) {
         yield call(overwriteFlow, url, schema, filePath, id, key.name, passphrase!);
      } else {
         yield put(pushErrorToast(errTitle));
      }
      //TODO
   }
}

function* overwriteFlow(
   url: string,
   schema: ObjectSchema<any>,
   filePath: string,
   keyID: string,
   name: string,
   passphrase?: string
) {
   try {
      const dialogResult: ICloseSubDialogAction<null> = yield call(openDialogWithResult, {
         id: DialogTypes.ConfirmationDialog.id,
         title: i18n.t('Key File Already Exists'),
         content: i18n.t('Do you want to replace it?'),
      });

      const shouldOverwrite = dialogResult.payload?.result === DialogResult.OK;

      if (shouldOverwrite) {
         yield call(
            asyncSagaConstructor(url, keyTypes.EXPORT, {
               getMethod: ApiService.postSecureJson,
               data: schema.cast(
                  {
                     keyID,
                     asciiArmored: filePath.indexOf(ASC_EXT) > 0,
                     filePath,
                     overwrite: true,
                     passphrase,
                  },
                  {stripUnknown: true}
               ),
            })
         );
         yield put(
            pushSuccessToast(`Key ${name} successfully exported to
      ${filePath}`)
         );
      }
   } catch (e) {
      yield call(unhandedSagaError, e as Error);
   }
}

function* getFilePath({
   title,
   filename,
   filesOfType,
}: {
   title: string;
   filename: string;
   filesOfType: string[];
}) {
   const exportDir: IDiplomatFile = yield call(
      asyncSagaConstructor(api.defaultKeyStore, keyTypes.GET_DEFAULT_KEY_STORE)
   ); // KeyManager.getInstance().getDefaultKeyStore();

   const file: string = yield call(getFilename, i18n.t('File Name'), i18n.t('File Name'), filename);

   if (!file) {
      return null;
   }

   const dbFilePathAction: ICloseSubDialogAction<string> = yield call(openDialogWithResult, {
      id: DialogTypes.FileManager.id,
      data: {
         title,
         // filesOfType,
         initialPath: exportDir.path,
         directoriesOnly: true,
      } as FileManagerDialogData,
   });

   return (
      isString(dbFilePathAction.payload?.data) &&
      `${dbFilePathAction.payload?.data}${FILE_SEPARATOR}${file}`
   );
}

export function* getFilename(title: string, label: string, value: string) {
   const passwordObj: ICloseSubDialogAction<NameFormType> = yield call(openDialogWithResult, {
      id: DialogTypes.InputDialog.id,
      data: {
         title,
         label,
         value,
      } as InputDialogData,
   });

   return passwordObj.payload?.data?.name;
}
